import React, { forwardRef } from 'react';
import styled from 'styled-components';

const SwitchLabel = styled.label`
  align-items: center;
  display: inline-flex;
  gap: 2.66em;
  height: 1.58em;
  position: relative;
  top: -0.1em;

  & input {
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const Switch = styled.span<{ $disabled?: boolean }>`
  position: absolute;
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 2.66em;
  background-color: ${props => (props.$disabled ? 'var(--color-secondaryText-40)' : 'var(--color-secondaryText)')};
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 1.4em;
  opacity: ${props => (props.$disabled ? 0.6 : 1)};

  &:before {
    position: absolute;
    content: '';
    height: 1.2em;
    width: 1.2em;
    inset-inline-start: 0.2em;
    bottom: 0.2em;
    background-color: white;
    -webkit-transition: 0.4s;
    transition: 0.3s;
    border-radius: 50%;
  }

  input:checked + & {
    background-color: ${props => (props.$disabled ? '#b0b0b0' : 'var(--color-primaryButton)')};
  }

  input:focus + & {
    box-shadow: 0 0 1px var(--color-primaryButton);
    outline: 2px solid var(--color-primaryButton);
    outline-offset: 2px;
  }

  input:checked + &:before {
    transform: translateX(1em);
  }
`;

const SwitchLabelText = styled.span<{ $disabled?: boolean }>`
  opacity: ${props => (props.$disabled ? 0.6 : 1)};
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
`;

type ToggleSwitchProps = {
  ariaLabel?: string;
  ariaLabelledBy?: string;
  checked?: boolean;
  datacy?: string;
  disabled?: boolean;
  label?: React.ReactNode;
  onSwitch: (checked: boolean) => void;
};

const ToggleSwitch = forwardRef<HTMLInputElement, ToggleSwitchProps>(
  ({ onSwitch, datacy, checked, label, ariaLabel, ariaLabelledBy, disabled = false }, ref) => (
    <SwitchLabel>
      <input
        ref={ref}
        type="checkbox"
        role="switch"
        checked={Boolean(checked)}
        disabled={Boolean(disabled)}
        {...(ariaLabel && { 'aria-label': ariaLabel })}
        {...(ariaLabelledBy && { 'aria-labelledby': ariaLabelledBy })}
        aria-checked={checked ? 'true' : 'false'}
        data-testid={datacy || 'ToggleSwitch'}
        onChange={e => onSwitch(e.target.checked)}
      />
      <Switch $disabled={disabled} data-testid="RouteToggleSwitch" />
      <SwitchLabelText $disabled={disabled}>{label}</SwitchLabelText>
    </SwitchLabel>
  ),
);

export default ToggleSwitch;
